// ============================================================================
// ENTITY TREE - Main Component
// Displays entities in a hierarchical tree with validation status
// ============================================================================

import { useState, useCallback, useMemo } from 'react'
import { useEntityTree, useEntityValidation, useEntityCreation, useEntitySuggestions, type UseEntityTreeOptions } from '@/hooks/useEntityTree'
import { useJourneyStore } from '@/stores/journeyStore'
import type { PhaseContextForSuggestions } from '@/lib/api/client'
import { EntityTreeHeader, type DomainFilter } from './EntityTreeHeader'
import { PendingBanner } from './PendingBanner'
import { DomainNode } from './DomainNode'
import { AISuggestionsPanel, type AISuggestion, type SelectedItem } from './AISuggestionsPanel'
import { Loader2 } from 'lucide-react'
import type { EntityTreeInstance, EntityTreeClassGroup } from '@/lib/api/client'

// ============================================================================
// TYPES
// ============================================================================

export interface EntityTreeProps {
  /** Initial mode */
  defaultMode?: 'structured' | 'free'
  /** Show the mode toggle */
  showModeToggle?: boolean
  /** Show the phase indicator */
  showPhaseIndicator?: boolean
  /** Show validation actions on entities */
  showValidationActions?: boolean
  /** Show progress bars on class nodes */
  showProgress?: boolean
  /** Show domain filter chips */
  showDomainFilter?: boolean
  /** Enable inline entity creation */
  showInlineAdd?: boolean
  /** Enable AI suggestions panel */
  showAISuggestions?: boolean
  /** Called when an entity is selected */
  onEntitySelect?: (entity: EntityTreeInstance) => void
  /** Currently selected entity URI */
  selectedEntityUri?: string
  /** Custom class name */
  className?: string
}

// ============================================================================
// COMPONENT
// ============================================================================

export function EntityTree({
  defaultMode = 'structured',
  showModeToggle = true,
  showPhaseIndicator = true,
  showValidationActions = true,
  showProgress = true,
  showDomainFilter = true,
  showInlineAdd = true,
  showAISuggestions = true,
  onEntitySelect,
  selectedEntityUri,
  className = '',
}: EntityTreeProps) {
  const [mode, setMode] = useState<'structured' | 'free'>(defaultMode)
  const [domainFilter, setDomainFilter] = useState<DomainFilter>({
    market: true,
    business: true,
    solution: true,
  })

  // AI suggestions state - now works with class selection
  const [selectedClass, setSelectedClass] = useState<EntityTreeClassGroup | null>(null)
  const [showSuggestionsPanel, setShowSuggestionsPanel] = useState(false)

  // Get journey context for phase indicator
  const getCurrentPhase = useJourneyStore((s) => s.getCurrentPhase)
  const currentPhase = getCurrentPhase()

  // Entity tree data with mode-based filtering
  const options: UseEntityTreeOptions = { mode }
  const {
    entities,
    byDomain,
    counts,
    phaseRequirements,
    isLoading,
    isError,
    error,
  } = useEntityTree(options)

  // Validation mutations
  const {
    confirmEntity,
    rejectEntity,
    confirmAllPending,
    isUpdating,
  } = useEntityValidation()

  // Entity creation mutation
  const { createEntityAsync } = useEntityCreation()

  // Build phase context for AI suggestions (only in structured mode)
  const phaseContext = useMemo((): PhaseContextForSuggestions | undefined => {
    if (mode !== 'structured' || !currentPhase) return undefined

    return {
      phaseName: currentPhase.name,
      phaseDescription: currentPhase.description,
      requiredEntities: phaseRequirements.map((req) => ({
        classUri: req.classUri,
        className: req.className,
        domain: req.domain,
        minCount: req.minCount,
        currentCount: req.currentCount,
      })),
    }
  }, [mode, currentPhase, phaseRequirements])

  // AI suggestions hook - use class URI for suggestions with phase context
  const {
    suggestions,
    isLoading: isSuggestionsLoading,
  } = useEntitySuggestions(
    showAISuggestions && selectedClass ? selectedClass.classUri : null,
    phaseContext
  )

  // Get pending entities for bulk actions
  const pendingEntities = entities.filter((e) => e.status === 'pending')

  // Handle validation actions
  const handleConfirm = (entityUri: string) => {
    confirmEntity(entityUri)
  }

  const handleReject = (entityUri: string) => {
    rejectEntity(entityUri)
  }

  const handleConfirmAll = () => {
    const pendingUris = pendingEntities.map((e) => e.uri)
    confirmAllPending(pendingUris)
  }

  // Handle inline entity creation
  const handleCreateEntity = async (classUri: string, label: string) => {
    // Extract entity type from class URI (e.g., "http://...#Persona" -> "Persona")
    const entityType = classUri.split('#').pop() || classUri
    await createEntityAsync({ entityType, label })
  }

  // Handle AI suggestions button click
  const handleAISuggestionsClick = useCallback((classGroup: EntityTreeClassGroup) => {
    setSelectedClass(classGroup)
    setShowSuggestionsPanel(true)
  }, [])

  // Handle accepting an AI suggestion (create as new entity)
  const handleAcceptSuggestion = useCallback(async (suggestion: AISuggestion) => {
    if (suggestion.entityType) {
      await createEntityAsync({
        entityType: suggestion.entityType,
        label: suggestion.name,
        description: suggestion.reason,
      })
    }
  }, [createEntityAsync])

  // Handle closing AI suggestions panel
  const handleCloseSuggestions = useCallback(() => {
    setShowSuggestionsPanel(false)
    setSelectedClass(null)
  }, [])

  // Check if any domains are visible
  const hasVisibleDomains =
    (domainFilter.market && byDomain.market.length > 0) ||
    (domainFilter.business && byDomain.business.length > 0) ||
    (domainFilter.solution && byDomain.solution.length > 0)

  // Loading state
  if (isLoading) {
    return (
      <div className={`flex items-center justify-center py-12 ${className}`}>
        <Loader2 className="h-6 w-6 animate-spin text-warm-400" />
      </div>
    )
  }

  // Error state
  if (isError) {
    return (
      <div className={`p-4 text-sm text-red-600 ${className}`}>
        Failed to load entities: {error?.message || 'Unknown error'}
      </div>
    )
  }

  // Determine effective selected class URI
  const effectiveSelectedClassUri = selectedClass?.classUri

  return (
    <div className={`flex gap-4 ${className}`}>
      {/* Main tree panel */}
      <div className="flex-1 flex flex-col bg-white rounded-lg border border-warm-200 overflow-hidden">
        {/* Header with mode toggle and domain filter */}
        <EntityTreeHeader
          mode={mode}
          onModeChange={setMode}
          showModeToggle={showModeToggle}
          currentPhaseName={showPhaseIndicator ? currentPhase?.name : undefined}
          phaseColor={currentPhase?.domain}
          domainFilter={domainFilter}
          onDomainFilterChange={setDomainFilter}
          showDomainFilter={showDomainFilter}
        />

        {/* Pending banner */}
        {counts.pending > 0 && showValidationActions && (
          <PendingBanner
            pendingCount={counts.pending}
            onReviewAll={handleConfirmAll}
            isUpdating={isUpdating}
          />
        )}

        {/* Tree content */}
        <div className="flex-1 overflow-y-auto p-2">
          {/* Market domain */}
          {domainFilter.market && (
            <DomainNode
              domain="market"
              classGroups={byDomain.market}
              phaseRequirements={phaseRequirements}
              showProgress={showProgress}
              showValidationActions={showValidationActions}
              showInlineAdd={showInlineAdd}
              showAISuggestions={showAISuggestions}
              onConfirm={handleConfirm}
              onReject={handleReject}
              onEntitySelect={onEntitySelect}
              onAISuggestionsClick={showAISuggestions ? handleAISuggestionsClick : undefined}
              selectedClassUri={effectiveSelectedClassUri}
              selectedEntityUri={selectedEntityUri}
              onCreateEntity={handleCreateEntity}
            />
          )}

          {/* Business domain */}
          {domainFilter.business && (
            <DomainNode
              domain="business"
              classGroups={byDomain.business}
              phaseRequirements={phaseRequirements}
              showProgress={showProgress}
              showValidationActions={showValidationActions}
              showInlineAdd={showInlineAdd}
              showAISuggestions={showAISuggestions}
              onConfirm={handleConfirm}
              onReject={handleReject}
              onEntitySelect={onEntitySelect}
              onAISuggestionsClick={showAISuggestions ? handleAISuggestionsClick : undefined}
              selectedClassUri={effectiveSelectedClassUri}
              selectedEntityUri={selectedEntityUri}
              onCreateEntity={handleCreateEntity}
            />
          )}

          {/* Solution domain */}
          {domainFilter.solution && (
            <DomainNode
              domain="solution"
              classGroups={byDomain.solution}
              phaseRequirements={phaseRequirements}
              showProgress={showProgress}
              showValidationActions={showValidationActions}
              showInlineAdd={showInlineAdd}
              showAISuggestions={showAISuggestions}
              onConfirm={handleConfirm}
              onReject={handleReject}
              onEntitySelect={onEntitySelect}
              onAISuggestionsClick={showAISuggestions ? handleAISuggestionsClick : undefined}
              selectedClassUri={effectiveSelectedClassUri}
              selectedEntityUri={selectedEntityUri}
              onCreateEntity={handleCreateEntity}
            />
          )}

          {/* Empty state */}
          {(counts.total === 0 || !hasVisibleDomains) && (
            <div className="py-8 text-center text-sm text-warm-500">
              {counts.total === 0 ? 'No entities found' : 'No entities match your filters'}
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 px-4 py-3 bg-warm-50 border-t border-warm-200 text-xs text-warm-500">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-market" />
            <span>Confirmed</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            <span>Pending</span>
          </div>
          {showProgress && (
            <div className="flex items-center gap-1.5">
              <div className="w-8 h-1 bg-warm-200 rounded overflow-hidden">
                <div className="w-3/5 h-full bg-market rounded" />
              </div>
              <span>Progress</span>
            </div>
          )}
        </div>
      </div>

      {/* AI Suggestions Panel */}
      {showAISuggestions && showSuggestionsPanel && selectedClass && (
        <AISuggestionsPanel
          selectedItem={{
            type: 'class',
            label: selectedClass.className,
            domain: selectedClass.domain,
            classUri: selectedClass.classUri,
            className: selectedClass.className,
          }}
          suggestions={suggestions as AISuggestion[]}
          isLoading={isSuggestionsLoading}
          onClose={handleCloseSuggestions}
          onAcceptSuggestion={handleAcceptSuggestion}
        />
      )}
    </div>
  )
}

export default EntityTree
